import type { Entity } from "@/lib/entities"

/**
 * EL ALCANCE: qué se está mirando, y quién lo decide.
 *
 * Éste es el punto 11 que quedaba abierto, y Cesar lo cerró él mismo:
 *
 *   «Los filtros en contabilidad, finanzas y dashboard ya deberían de venir
 *   aplicados como default a la sociedad que estás viendo (la seleccionada
 *   arriba). […] si es un cliente total y cada sociedad me parece correcto,
 *   pero que en dashboard sí se pueda ver las 4 sociedades juntas; en la parte
 *   financiera y contable sí debería de ser por sociedad, y luego si quieres
 *   ver suma vas a holding.»
 *
 * De ahí salen CUATRO reglas, y son las que implementa este módulo:
 *
 *   Dashboard      por sociedad por defecto, con «las N juntas» disponible.
 *   Finanzas       por sociedad, siempre. Sin opción de consolidar.
 *   Contabilidad   por sociedad, siempre. Ya lo era: `entity_id` es obligatorio
 *                  en el libro y la SPA lo toma del selector de arriba.
 *   Holding        la suma, y sólo ahí. Convierte monedas y elimina lo que las
 *                  sociedades se facturan entre ellas.
 *
 * Lo que esto arregla es un fallo silencioso: hasta hoy Finanzas y Dashboard
 * arrancaban en «Consolidado» mientras la barra de arriba decía una sociedad
 * concreta. Las dos coordenadas se contradecían en pantalla y ganaba la de
 * abajo — así que se leía el total del grupo creyendo estar mirando Perú.
 *
 * ===========================================================================
 * LA TRAMPA: EL MODELO FINANCIERO SE LLEVA POR PAÍS, NO POR SOCIEDAD
 * ===========================================================================
 *
 * `financial_fact` no tiene `entity_id`. Su clave es
 * `(tenant, origen, PAIS, bloque, sub_account, año, mes)`: la hoja de gestión de
 * la que sale trae una columna por país, no por sociedad, así que la separación
 * por sociedad **no existe en el dato**.
 *
 * Hoy eso no se nota porque Siete tiene exactamente una sociedad en cada país
 * —PE, MX, BR y PA—, y ahí país y sociedad son la misma cosa. Pero es una
 * coincidencia de este cliente, no una propiedad del sistema: el día que haya
 * dos sociedades peruanas, filtrar por una devolverá la suma de las dos.
 *
 * Y ESO NO SE PUEDE ARREGLAR AQUÍ. Ponerle `entity_id` a `financial_fact` sería
 * inventar una precisión que la fuente no tiene: habría que repartir entre dos
 * sociedades unas cifras que llegaron sumadas. Lo que sí se puede hacer —y es lo
 * que hace `alcanceFinanciero`— es DETECTARLO y decirlo en pantalla, en vez de
 * enseñar la suma de dos empresas con el nombre de una.
 *
 * La solución de verdad es que la hoja traiga la sociedad. Está anotado.
 */

/** La sociedad activa resuelta contra el catálogo. `null` si no hay o no está. */
export function sociedadActiva(
  entities: Entity[],
  activeEntityId: string | null,
): Entity | null {
  if (!activeEntityId) return null
  return entities.find((e) => e.id === activeEntityId) ?? null
}

export interface AlcanceFinanciero {
  /** La sociedad que manda, la del selector de arriba. */
  sociedad: Entity | null
  /**
   * El país con el que hay que consultar `financial_fact`. `null` cuando no hay
   * sociedad elegida todavía —o no se sabe su país—, y entonces NO se consulta:
   * mejor pedir que elija que devolver el consolidado disfrazado de una.
   */
  pais: string | null
  /**
   * Las OTRAS sociedades que comparten país con la activa. Vacío casi siempre.
   * Si trae algo, las cifras de esta pantalla las incluyen y hay que decirlo:
   * ver la nota larga de arriba.
   */
  comparten: Entity[]
}

export function alcanceFinanciero(
  entities: Entity[],
  activeEntityId: string | null,
): AlcanceFinanciero {
  const sociedad = sociedadActiva(entities, activeEntityId)
  const pais = sociedad?.country ?? null
  const comparten = pais
    ? entities.filter((e) => e.id !== sociedad?.id && e.country === pais)
    : []
  return { sociedad, pais, comparten }
}

/**
 * EL ALCANCE DEL DASHBOARD, que es el único que admite «todas».
 *
 * `"TODAS"` es un valor propio y no la cadena vacía a propósito. Vacío significa
 * «no hay filtro puesto», y ese es justo el estado que producía el error: se
 * leía como «todavía no elegí» y se comportaba como «todo el grupo». Un valor
 * con nombre obliga a que alguien lo haya elegido.
 */
export const TODAS = "TODAS" as const

export type AlcanceDashboard = string

/**
 * Con qué alcance arranca el dashboard: la sociedad de arriba.
 *
 * Y NO «TODAS», aunque el dashboard sea la pantalla que sí puede enseñarlas
 * juntas. Cesar pidió que el filtro «venga aplicado como default a la sociedad
 * que estás viendo»: poder ver las cuatro es una opción, no el punto de partida.
 */
export function alcanceInicialDashboard(
  entities: Entity[],
  activeEntityId: string | null,
): AlcanceDashboard {
  const sociedad = sociedadActiva(entities, activeEntityId)
  return sociedad?.country ?? TODAS
}

/**
 * El país que se le manda al backend para un alcance del dashboard.
 *
 * `TODAS` viaja como cadena vacía porque es lo que los endpoints de `finance`
 * entienden por consolidado (`_filtro_pais` omite el `AND pais = :pais`). La
 * traducción se hace aquí y en un solo sitio: que el valor con nombre viva en la
 * pantalla y el vacío sólo en el borde de la API es lo que evita volver a
 * confundir «sin elegir» con «todo».
 */
export function paisDeConsulta(alcance: AlcanceDashboard): string {
  return alcance === TODAS ? "" : alcance
}
